import {ApolloError} from '@sail/data';
import type {MutationMeta, QueryMeta} from 'react-query';
import {MutationCache, QueryCache, QueryClient} from 'react-query';
import type {SentryTeamName} from '@stripe-internal/connect-embedded-lib';
import type * as Sentry from '@sentry/browser';
import type {BaseConnectElementParams} from './buildAnalyticsSender';
import {buildAnalyticsSender} from './buildAnalyticsSender';

import type {PrimitiveRecord} from '../utils/errorHandling';
import {
  EmbeddedError,
  getFirstSafeElement,
  isExpectedErrorsType,
  makeSafe,
} from '../utils/errorHandling';
import type {ConnectElementImportKeys} from './ConnectJSInterface/ConnectElementList';
import {
  reportQueryOrMutationError,
  reportQueryOrMutationSuccess,
} from '../utils/telemetry/connectElementAnalyticsSender';
import {buildFetchOverride} from '../data-layer-client/buildFetchOverride';
import {isSubmerchantAuthExpectedError} from '../data-layer-client/SubmerchantAuthenticationError';
import {getDevLogger} from '../utils/getLogger';
import {
  getTeamForComponent,
  isValidSentryTeamName,
} from '../utils/getTeamForComponent';
import {getHostApp} from '../utils/getHostApp';
import createReportErrorOrSuccessLink from '../graphql/links/createReportErrorOrSuccessLink';
import type {ConnectJsOptions} from './ConnectJsOptions';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';

export const setupRender = ({
  connectElementKey,
  connectJsOptions,
  frameMessenger,
}: {
  connectElementKey: ConnectElementImportKeys;
  connectJsOptions: ConnectJsOptions;
  frameMessenger: IFrameMessenger;
}) => {
  const {metaOptions} = connectJsOptions.getValues();

  const fetch = buildFetchOverride(
    frameMessenger.proxyRequest,
    connectJsOptions,
    connectElementKey,
  );

  const teamName = getTeamForComponent(connectElementKey);

  // WARNING! Keep in sync with `src/connect/getObservabilityConfig.ts` and the
  // `config.analytics.parameters` key.
  const baseParams: BaseConnectElementParams = {
    connectElement: connectElementKey,
    hostApp: metaOptions?.hostApp ?? 'platform',
    mobileSdkBundleAppId: metaOptions?.mobileSdkBundleAppId,
    teamName,
  };

  const connectElementAnalyticsSender = buildAnalyticsSender(
    frameMessenger.sendAnalytics,
    baseParams,
  );

  const reportError = (
    error: Error,
    tags?: PrimitiveRecord,
    extra?: PrimitiveRecord,
    owner?: SentryTeamName,
    level: Sentry.SeverityLevel = 'error',
  ) => {
    const modifiedTags = {
      type: EmbeddedError.Error,
      ...tags,
      ...baseParams,
    };

    const modifiedExtra = {
      ...extra,
      ...baseParams,
    };

    if (isSubmerchantAuthExpectedError(error)) {
      getDevLogger().warn('Expected error:', error);
    } else {
      frameMessenger.sendErrorReport({
        error,
        connectElement: connectElementKey,
        tags: modifiedTags,
        extra: modifiedExtra,
        owner,
        level,
      });

      if (level === 'error') {
        connectElementAnalyticsSender.track(
          'submerchant_surfaces_total_errors',
          {
            type: modifiedTags.type,
          },
        );
      }
    }
  };

  const handleQueryClientErrors = (
    error: unknown,
    queryOrMutationKey: string,
    queryOrMutationMeta: MutationMeta | QueryMeta | undefined,
  ) => {
    const meta = makeSafe(queryOrMutationMeta?.errorData);
    const expectedErrors = isExpectedErrorsType(
      queryOrMutationMeta?.expectedErrors,
    )
      ? queryOrMutationMeta?.expectedErrors
      : undefined;
    const mutedErrors = isExpectedErrorsType(queryOrMutationMeta?.mutedErrors)
      ? queryOrMutationMeta?.mutedErrors
      : undefined;
    const owner = isValidSentryTeamName(queryOrMutationMeta?.owner)
      ? queryOrMutationMeta?.owner
      : undefined;
    const component = queryOrMutationMeta?.component
      ? makeSafe(queryOrMutationMeta?.component)
      : undefined;
    const hostApp = getHostApp(metaOptions);

    if (error instanceof ApolloError) {
      error.graphQLErrors.forEach((error) => {
        reportQueryOrMutationError(
          connectElementAnalyticsSender,
          reportError,
          error,
          queryOrMutationKey,
          hostApp,
          expectedErrors,
          mutedErrors,
          meta,
          undefined,
          owner,
          component,
        );
      });
    } else {
      reportQueryOrMutationError(
        connectElementAnalyticsSender,
        reportError,
        error,
        queryOrMutationKey,
        hostApp,
        expectedErrors,
        mutedErrors,
        meta,
        undefined,
        owner,
        component,
      );
    }
  };

  // Remove once all queries are migrated to GraphQL
  const queryClient = new QueryClient({
    // Disable default refetch on window focus + retry behavior to match our graphql client
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const queryOrMutationKey = getFirstSafeElement(
          mutation.options.mutationKey,
        );
        handleQueryClientErrors(error, queryOrMutationKey, mutation?.meta);
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        const owner = isValidSentryTeamName(mutation?.meta?.owner)
          ? mutation?.meta?.owner
          : undefined;
        const component = mutation?.meta?.component
          ? makeSafe(mutation?.meta?.component)
          : undefined;

        const queryOrMutationKey = getFirstSafeElement(
          mutation.options.mutationKey,
        );
        reportQueryOrMutationSuccess(
          connectElementAnalyticsSender,
          queryOrMutationKey,
          undefined,
          owner,
          component,
        );
      },
    }),
    queryCache: new QueryCache({
      // Global error handler for all Queries that use this client
      onError: (error, query) => {
        const queryOrMutationKey = getFirstSafeElement(query.queryKey);
        handleQueryClientErrors(error, queryOrMutationKey, query?.meta);
      },
      onSuccess: (_data, query) => {
        const owner = isValidSentryTeamName(query?.meta?.owner)
          ? query?.meta?.owner
          : undefined;
        const component = query?.meta?.component
          ? makeSafe(query?.meta?.component)
          : undefined;

        const queryOrMutationKey = getFirstSafeElement(query.queryKey);
        reportQueryOrMutationSuccess(
          connectElementAnalyticsSender,
          queryOrMutationKey,
          undefined,
          owner,
          component,
        );
      },
    }),
  });

  const reportErrorOrSuccessLink = createReportErrorOrSuccessLink({
    connectElementAnalyticsSender,
    reportError,
    hostApp: getHostApp(metaOptions),
  });

  return {
    fetch,
    reportError,
    reportErrorOrSuccessLink,
    queryClient,
  };
};
